import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Add, DeleteOutline } from "@mui/icons-material";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Axios from "../axios";

const FUEL_YOUR_RACE_TYPES = ["Nutrition", "Hydration", "Meal Prep", "Recovery", "Apparel"];

const blankPlan = {
  planName: "",
  type: "",
  subtitle: "",
  description: "",
  price: "",
  discountPercentage: "",
  couponCode: "",
  profileimg: null,
  coverimage: null,
  whatYouGet: [""],
};

const FuelYourRaceForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND;
  const [plan, setPlan] = useState(blankPlan);
  const [profilePreview, setProfilePreview] = useState("");
  const [coverPreview, setCoverPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const showError = (err) => {
    toast.error(err?.response?.data?.message || err?.response?.data?.error || "Something went wrong");
  };

  useEffect(() => {
    let url;
    if (plan.profileimg instanceof File) {
      url = URL.createObjectURL(plan.profileimg);
      setProfilePreview(url);
    } else {
      setProfilePreview(plan.profileimg || "");
    }
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [plan.profileimg]);

  useEffect(() => {
    let url;
    if (plan.coverimage instanceof File) {
      url = URL.createObjectURL(plan.coverimage);
      setCoverPreview(url);
    } else {
      setCoverPreview(plan.coverimage || "");
    }
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [plan.coverimage]);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    Axios.get(`${BACKEND_URL}fuel-your-race/${id}`)
      .then((res) => {
        const data = res?.data?.data || {};
        setPlan({
          planName: data.planName || "",
          type: data.type || "",
          subtitle: data.subtitle || "",
          description: data.description || "",
          price: data.price ?? "",
          discountPercentage: data.discountPercentage ?? "",
          couponCode: data.couponCode || "",
          profileimg: data.profileimg || null,
          coverimage: data.coverimage || null,
          whatYouGet: Array.isArray(data.whatYouGet) && data.whatYouGet.length ? data.whatYouGet : [""],
        });
      })
      .catch(showError)
      .finally(() => setLoading(false));
  }, [BACKEND_URL, id, isEdit]);

  const updatePlan = (key, value) => {
    setPlan((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const updateWhatYouGet = (index, value) => {
    setPlan((prev) => ({
      ...prev,
      whatYouGet: prev.whatYouGet.map((item, itemIndex) => (itemIndex === index ? value : item)),
    }));
    setErrors((prev) => ({ ...prev, [`whatYouGet_${index}`]: "" }));
  };

  const addWhatYouGet = () => {
    setPlan((prev) => ({
      ...prev,
      whatYouGet: [...prev.whatYouGet, ""],
    }));
  };

  const removeWhatYouGet = (index) => {
    setPlan((prev) => ({
      ...prev,
      whatYouGet: prev.whatYouGet.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const validate = () => {
    const nextErrors = {};

    if (!plan.planName.trim()) nextErrors.planName = "Plan Name is required";
    if (!plan.type) nextErrors.type = "Type is required";
    if (!plan.subtitle.trim()) nextErrors.subtitle = "Subtitle is required";
    if (!plan.description.trim()) nextErrors.description = "Description is required";

    if (!String(plan.price).trim()) {
      nextErrors.price = "Price is required";
    } else if (Number.isNaN(Number(plan.price)) || Number(plan.price) < 0) {
      nextErrors.price = "Price must be 0 or greater";
    }

    if (!String(plan.discountPercentage).trim()) {
      nextErrors.discountPercentage = "% Off is required";
    } else if (
      Number.isNaN(Number(plan.discountPercentage)) ||
      Number(plan.discountPercentage) < 0 ||
      Number(plan.discountPercentage) > 100
    ) {
      nextErrors.discountPercentage = "% Off must be between 0 and 100";
    }

    if (!plan.couponCode.trim()) nextErrors.couponCode = "Coupon Code is required";

    if (!plan.profileimg) {
      nextErrors.profileimg = "Profile image is required";
    } else if (plan.profileimg instanceof File) {
      if (!plan.profileimg.type.startsWith("image/")) nextErrors.profileimg = "Profile image must be a valid image file";
      else if (plan.profileimg.size > 2 * 1024 * 1024) nextErrors.profileimg = "Profile image exceeds 2MB limit";
    }

    if (!plan.coverimage) {
      nextErrors.coverimage = "Cover image is required";
    } else if (plan.coverimage instanceof File) {
      if (!plan.coverimage.type.startsWith("image/")) nextErrors.coverimage = "Cover image must be a valid image file";
      else if (plan.coverimage.size > 2 * 1024 * 1024) nextErrors.coverimage = "Cover image exceeds 2MB limit";
    }

    (plan.whatYouGet || []).forEach((point, index) => {
      if (!point.trim()) nextErrors[`whatYouGet_${index}`] = "This field is required";
    });

    return nextErrors;
  };

  const submit = () => {
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      toast.error("Please fill out the mandatory fields");
      return;
    }

    setLoading(true);
    const payload = new FormData();
    payload.append("planName", plan.planName.trim());
    payload.append("type", plan.type);
    payload.append("subtitle", plan.subtitle.trim());
    payload.append("description", plan.description.trim());
    payload.append("price", plan.price === "" ? 0 : plan.price);
    payload.append("discountPercentage", plan.discountPercentage === "" ? 0 : plan.discountPercentage);
    payload.append("couponCode", plan.couponCode.trim());
    payload.append(
      "whatYouGet",
      JSON.stringify((plan.whatYouGet || []).map((item) => item.trim()).filter(Boolean))
    );

    if (plan.profileimg instanceof File) payload.append("profileimg", plan.profileimg);
    if (plan.coverimage instanceof File) payload.append("coverimage", plan.coverimage);

    const request = isEdit
      ? Axios.put(`${BACKEND_URL}fuel-your-race/${id}`, payload, { headers: { "Content-Type": "multipart/form-data" } })
      : Axios.post(`${BACKEND_URL}fuel-your-race`, payload, { headers: { "Content-Type": "multipart/form-data" } });

    request
      .then((res) => {
        toast.success(res?.data?.message || "Saved successfully");
        navigate("/fuel-your-race-management");
      })
      .catch(showError)
      .finally(() => setLoading(false));
  };

  return (
    <div className="container-fluid">
      <Helmet>
        <title>{isEdit ? "Edit Plan" : "Create Fuel Your Race Plan"}</title>
      </Helmet>
      <div className="d-flex justify-content-between align-center mb-3">
        <h2>{isEdit ? "Edit Plan" : "Create Fuel Your Race Plan"}</h2>
        <Button onClick={() => navigate("/fuel-your-race-management")}>Back</Button>
      </div>

      <Typography variant="h6" sx={{ mb: 2 }}>General Details</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="Plan Name"
            value={plan.planName}
            onChange={(e) => updatePlan("planName", e.target.value)}
            error={Boolean(errors.planName)}
            helperText={errors.planName}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="Subtitle"
            value={plan.subtitle}
            onChange={(e) => updatePlan("subtitle", e.target.value)}
            error={Boolean(errors.subtitle)}
            helperText={errors.subtitle}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth required error={Boolean(errors.type)}>
            <InputLabel id="fuel-your-race-type-label">Type</InputLabel>
            <Select
              labelId="fuel-your-race-type-label"
              label="Type"
              value={plan.type}
              onChange={(e) => updatePlan("type", e.target.value)}
            >
              {FUEL_YOUR_RACE_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
            {errors.type && <Typography variant="caption" color="error" sx={{ ml: 1.75 }}>{errors.type}</Typography>}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            multiline
            minRows={3}
            label="Description"
            value={plan.description}
            onChange={(e) => updatePlan("description", e.target.value)}
            error={Boolean(errors.description)}
            helperText={errors.description}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            required
            type="number"
            label="Price"
            value={plan.price}
            onChange={(e) => updatePlan("price", e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            error={Boolean(errors.price)}
            helperText={errors.price}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            required
            type="number"
            label="% Off"
            value={plan.discountPercentage}
            onChange={(e) => updatePlan("discountPercentage", e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            error={Boolean(errors.discountPercentage)}
            helperText={errors.discountPercentage}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            required
            label="Coupon Code"
            value={plan.couponCode}
            onChange={(e) => updatePlan("couponCode", e.target.value)}
            error={Boolean(errors.couponCode)}
            helperText={errors.couponCode}
          />
        </Grid>
      </Grid>

      {/* Images Section */}
      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Images (Cover & Profile Image)</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Profile Image</Typography>
              {profilePreview ? (
                <Box sx={{ mb: 1, borderRadius: 1, overflow: "hidden", border: "1px solid #ddd", width: "100%", maxWidth: 300 }}>
                  <img src={profilePreview} alt="Profile preview" style={{ width: "100%", maxHeight: 200, objectFit: "contain", display: "block", backgroundColor: "#f7f7f7" }} />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: "italic" }}>
                  No profile image selected.
                </Typography>
              )}
              <TextField
                type="file"
                fullWidth
                required
                inputProps={{ accept: "image/*" }}
                onChange={(e) => updatePlan("profileimg", e.target.files?.[0] || null)}
                error={Boolean(errors.profileimg)}
                helperText={errors.profileimg || (plan.profileimg instanceof File ? plan.profileimg.name : "Upload profile image (max 2MB)")}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Cover Image</Typography>
              {coverPreview ? (
                <Box sx={{ mb: 1, borderRadius: 1, overflow: "hidden", border: "1px solid #ddd", width: "100%", maxWidth: 300 }}>
                  <img src={coverPreview} alt="Cover preview" style={{ width: "100%", maxHeight: 200, objectFit: "contain", display: "block", backgroundColor: "#f7f7f7" }} />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: "italic" }}>
                  No cover image selected.
                </Typography>
              )}
              <TextField
                type="file"
                fullWidth
                required
                inputProps={{ accept: "image/*" }}
                onChange={(e) => updatePlan("coverimage", e.target.files?.[0] || null)}
                error={Boolean(errors.coverimage)}
                helperText={errors.coverimage || (plan.coverimage instanceof File ? plan.coverimage.name : "Upload cover image (max 2MB)")}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* What You Get Section */}
      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>What You Get</Typography>
      {(plan.whatYouGet || []).map((point, index) => (
        <Box key={`what-you-get-${index}`} sx={{ display: "flex", gap: 1, mb: 1 }}>
          <TextField
            fullWidth
            required
            label={`Point ${index + 1}`}
            placeholder="e.g. Personalized training plan"
            value={point}
            onChange={(e) => updateWhatYouGet(index, e.target.value)}
            error={Boolean(errors[`whatYouGet_${index}`])}
            helperText={errors[`whatYouGet_${index}`]}
          />
          <IconButton onClick={() => removeWhatYouGet(index)} disabled={plan.whatYouGet.length === 1}>
            <DeleteOutline />
          </IconButton>
        </Box>
      ))}
      <Button startIcon={<Add />} sx={{ mt: 1 }} onClick={addWhatYouGet}>
        Add Point
      </Button>

      <Box sx={{ mt: 4, mb: 4, display: "flex", gap: 2 }}>
        <Button variant="contained" disabled={loading} onClick={submit}>
          {loading ? "Saving..." : isEdit ? "Update" : "Submit"}
        </Button>
        <Button onClick={() => navigate("/fuel-your-race-management")}>Cancel</Button>
      </Box>
    </div>
  );
};

export default FuelYourRaceForm;
